import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Phone, Mail, CreditCard, Award, BookOpen, Calendar, Check, X, Waves, Sparkles, MessageCircle,
  UserCog, GraduationCap, User, Trash2, Ban, ImageOff, Pencil, Clock, RefreshCw, Merge, Search, Users,
} from "lucide-react";
import { toast } from "sonner";
import Logbook from "./Logbook";
import ProfessorFeedback from "./professor/ProfessorFeedback";
import { Checkbox } from "../components/ui/checkbox";

const RANKING_LEVELS = ["Branco", "Amarelo", "Laranja", "Verde", "Azul", "Vermelho", "Preto"];
const RANKING_COLORS = {
  Branco: "bg-gray-100 text-gray-700 border-gray-300",
  Amarelo: "bg-yellow-100 text-yellow-800 border-yellow-400",
  Laranja: "bg-orange-100 text-orange-800 border-orange-400",
  Verde: "bg-green-100 text-green-800 border-green-400",
  Azul: "bg-blue-100 text-blue-800 border-blue-400",
  Vermelho: "bg-red-100 text-red-800 border-red-400",
  Preto: "bg-gray-900 text-white border-gray-900",
};

const StudentProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextLesson, setNextLesson] = useState(null);
  const [lastLesson, setLastLesson] = useState(null);

  // Dialogs
  const [isRankingDialogOpen, setIsRankingDialogOpen] = useState(false);
  const [rankingSurf, setRankingSurf] = useState("");
  const [rankingBodyboard, setRankingBodyboard] = useState("");
  const [isCreditsDialogOpen, setIsCreditsDialogOpen] = useState(false);
  const [creditAction, setCreditAction] = useState("add");
  const [creditAmount, setCreditAmount] = useState(5);
  const [creditType, setCreditType] = useState("credits");
  const [creditReason, setCreditReason] = useState("Ajuste manual");
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [feedbackLessonId, setFeedbackLessonId] = useState(null);  // Pre-selected lesson for feedback
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const [mensalidades, setMensalidades] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");

  // Merge students
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState("");
  const [mergeSearchResults, setMergeSearchResults] = useState([]);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergeTargetPreview, setMergeTargetPreview] = useState(null);
  const [sourcePreview, setSourcePreview] = useState(null);
  const [mergeOptions, setMergeOptions] = useState({
    keep_email: "target",
    keep_name: "target",
    keep_phone: "target",
    transfer_credits: true,
    transfer_private_credits: true,
    transfer_enrollments: true,
    transfer_feedback: true,
    transfer_purchases: true,
    transfer_subscriptions: true,
    transfer_rankings: true,
  });
  const [merging, setMerging] = useState(false);

  // Inline editing
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProfile(); }, [userId]); // fetchProfile uses userId internally

  const fetchProfile = async () => {
    try {
      const [profileRes, servicesRes] = await Promise.all([
        axios.get(`${API}/staff/students/${userId}`, { withCredentials: true }),
        axios.get(`${API}/services`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const p = profileRes.data;
      setProfile(p);
      setRankingSurf(p.ranking_surf || "");
      setRankingBodyboard(p.ranking_bodyboard || "");
      setEditName(p.name || "");
      setEditPhone(p.phone || "");
      setEditEmail(p.email || "");
      setMensalidades(servicesRes.data.filter(s => s.category === "mensalidade"));

      // Compute next and last lesson from enrollments
      const today = new Date().toISOString().slice(0, 10);
      const active = (p.enrollments || []).filter(e => ["enrolled", "reserved", "confirmed"].includes(e.status) && e.lesson?.date >= today);
      const past = (p.enrollments || []).filter(e => ["confirmed", "completed", "attended"].includes(e.status) && e.lesson?.date < today);
      active.sort((a, b) => (a.lesson?.date || "").localeCompare(b.lesson?.date || ""));
      past.sort((a, b) => (b.lesson?.date || "").localeCompare(a.lesson?.date || ""));
      setNextLesson(active[0] || null);
      setLastLesson(past[0] || null);
    } catch {
      toast.error("Erro ao carregar perfil");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRanking = async (modality, ranking) => {
    try {
      await axios.patch(`${API}/staff/users/${userId}/ranking`, { modality, ranking: ranking || null }, { withCredentials: true });
      toast.success(`Ranking de ${modality} atualizado!`);
      if (modality === "surf") setRankingSurf(ranking);
      else setRankingBodyboard(ranking);
      fetchProfile();
    } catch { toast.error("Erro ao atualizar ranking"); }
  };

  const handleUpdateCredits = async () => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/credits`, { action: creditAction, amount: creditAmount, reason: creditReason, credit_type: creditType }, { withCredentials: true });
      toast.success("Créditos atualizados!");
      setIsCreditsDialogOpen(false);
      fetchProfile();
    } catch { toast.error("Erro ao atualizar créditos"); }
  };

  const handleChangeRole = async (newRole) => {
    if (!window.confirm(`Tornar ${profile.name} ${newRole}?`)) return;
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success(`Role alterado para ${newRole}`);
      fetchProfile();
    } catch { toast.error("Erro ao alterar role"); }
  };

  const handleBlockToggle = async () => {
    const blocked = !profile.blocked;
    try {
      await axios.patch(`${API}/admin/users/${userId}/block`, { blocked }, { withCredentials: true });
      toast.success(blocked ? "Aluno bloqueado" : "Aluno desbloqueado");
      fetchProfile();
    } catch { toast.error("Erro ao alterar estado"); }
  };

  const handleRemoveUser = async () => {
    if (!window.confirm(`Remover ${profile.name}? Esta ação é irreversível.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("Utilizador removido");
      navigate(-1);
    } catch { toast.error("Erro ao remover utilizador"); }
  };

  const handleDeletePicture = async () => {
    if (!window.confirm("Remover a foto de perfil deste aluno?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}/picture`, { withCredentials: true });
      toast.success("Foto removida");
      fetchProfile();
    } catch { toast.error("Erro ao remover foto"); }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/profile`, { name: editName, phone: editPhone, email: editEmail }, { withCredentials: true });
      toast.success("Perfil atualizado");
      setIsEditing(false);
      fetchProfile();
    } catch (err) { 
      const msg = err.response?.data?.detail || "Erro ao atualizar perfil";
      toast.error(msg);
    }
  };

  const handleAssignSubscription = async () => {
    try {
      const payload = { user_id: userId };
      if (selectedPlan && selectedPlan !== "default") payload.plan_id = selectedPlan;
      await axios.post(`${API}/subscriptions`, payload, { withCredentials: true });
      toast.success("Mensalidade atribuída com sucesso!");
      setIsSubscriptionDialogOpen(false);
      setSelectedPlan("");
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao atribuir mensalidade"); }
  };

  // Merge students functions
  const openMergeDialog = async () => {
    setIsMergeDialogOpen(true);
    setMergeSearch("");
    setMergeSearchResults([]);
    setMergeTarget(null);
    setMergeTargetPreview(null);
    // Load source preview
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/merge-preview`, { withCredentials: true });
      setSourcePreview(res.data);
    } catch (err) {
      toast.error("Erro ao carregar dados do utilizador");
    }
  };

  const searchMergeTargets = async (query) => {
    setMergeSearch(query);
    if (query.length < 2) {
      setMergeSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/students?search=${encodeURIComponent(query)}&limit=10`, { withCredentials: true });
      // Filter out current user
      const filtered = (res.data.students || []).filter(s => s.user_id !== userId);
      setMergeSearchResults(filtered);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const selectMergeTarget = async (student) => {
    setMergeTarget(student);
    setMergeSearch("");
    setMergeSearchResults([]);
    // Load target preview
    try {
      const res = await axios.get(`${API}/admin/users/${student.user_id}/merge-preview`, { withCredentials: true });
      setMergeTargetPreview(res.data);
    } catch (err) {
      toast.error("Erro ao carregar dados do utilizador alvo");
    }
  };

  const handleMerge = async () => {
    if (!mergeTarget || !sourcePreview) return;
    if (!window.confirm(`Tem a certeza que quer fundir "${profile.name}" com "${mergeTarget.name}"? Esta ação é irreversível.`)) return;
    
    setMerging(true);
    try {
      const payload = {
        source_user_id: userId, // Current profile will be deleted
        target_user_id: mergeTarget.user_id, // This will remain
        ...mergeOptions
      };
      await axios.post(`${API}/admin/users/merge`, payload, { withCredentials: true });
      toast.success("Utilizadores fundidos com sucesso!");
      setIsMergeDialogOpen(false);
      // Navigate to the merged user
      navigate(`/admin/students/${mergeTarget.user_id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao fundir utilizadores";
      toast.error(msg);
    } finally {
      setMerging(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!profile) return null;

  const completedEnrollments = profile.enrollments?.filter(e => ["confirmed", "completed", "attended"].includes(e.status)) || [];
  const activeEnrollments = profile.enrollments?.filter(e => ["enrolled", "reserved"].includes(e.status)) || [];
  const isAdmin = currentUser?.role === "admin";
  const isStaff = ["admin", "professor"].includes(currentUser?.role);

  const LessonHighlight = ({ enrollment, label, color, icon: Icon }) => {
    if (!enrollment?.lesson) return null;
    const l = enrollment.lesson;
    return (
      <div className={`flex items-center gap-3 p-3 rounded-xl ${color}`} data-testid={`highlight-${label}`}>
        <div className="w-9 h-9 rounded-lg bg-white/80 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-[#3d5a63]" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]/70">{label}</p>
          <p className="text-sm font-medium text-[#3d5a63] truncate">{l.title || "Aula"}</p>
          <p className="text-xs text-[#94A3B8]">{l.date && format(parseISO(l.date), "d MMM yyyy", { locale: pt })} — {l.start_time}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#6697a4]" data-testid="back-btn">
        <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
      </Button>

        {/* Profile Card */}
        <Card className="bg-white border-0 shadow-sm rounded-2xl mb-6" data-testid="student-profile-card">
          <CardContent className="p-5 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              {/* Avatar with delete option */}
              <div className="relative group shrink-0">
                <Avatar className="h-20 w-20 sm:h-24 sm:w-24 ring-4 ring-[#6697a4]/20">
                  <AvatarImage src={profile.picture} />
                  <AvatarFallback className="bg-[#6697a4] text-white text-xl sm:text-2xl">{profile.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                {isAdmin && profile.picture && (
                  <button
                    onClick={handleDeletePicture}
                    className="absolute -top-1 -right-1 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    title="Remover foto"
                    data-testid="delete-photo-btn"
                  >
                    <ImageOff className="h-3 w-3" />
                  </button>
                )}
              </div>

              <div className="flex-1 min-w-0 w-full">
                {/* Name & badges */}
                {isEditing ? (
                  <div className="space-y-2 mb-3">
                    <div>
                      <Label className="text-xs text-[#94A3B8]">Nome</Label>
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#94A3B8]">Email</Label>
                      <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="email@exemplo.com" data-testid="edit-email-input" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#94A3B8]">Telefone</Label>
                      <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="Telefone" data-testid="edit-phone-input" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveProfile} className="bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="save-profile-btn"><Check className="mr-1 h-3 w-3" /> Guardar</Button>
                      <Button size="sm" variant="outline" onClick={() => { setIsEditing(false); setEditName(profile.name || ""); setEditEmail(profile.email || ""); setEditPhone(profile.phone || ""); }} data-testid="cancel-edit-btn"><X className="mr-1 h-3 w-3" /> Cancelar</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h1 className="text-xl sm:text-2xl font-bold text-[#3d5a63]">{profile.name}</h1>
                      <Badge variant="outline" className="text-[#6697a4]">{profile.role}</Badge>
                      {profile.blocked && <Badge className="bg-red-500 text-white border-0">Bloqueado</Badge>}
                      {isAdmin && (
                        <button onClick={() => setIsEditing(true)} className="text-[#94A3B8] hover:text-[#6697a4] transition-colors" data-testid="edit-profile-btn">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-sm text-[#475569] mb-3">
                      <span className="flex items-center gap-1 truncate"><Mail className="h-3.5 w-3.5 shrink-0" />{profile.email}</span>
                      {profile.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 shrink-0" />{profile.phone}</span>}
                      {profile.created_at && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5 shrink-0" />Desde {format(parseISO(profile.created_at), "MMM yyyy", { locale: pt })}</span>}
                    </div>
                  </>
                )}

                {/* Rankings */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {profile.ranking_surf ? (
                    <Badge className={`border ${RANKING_COLORS[profile.ranking_surf] || ""}`} data-testid="profile-ranking-surf"><Waves className="mr-1 h-3 w-3" /> Surf: {profile.ranking_surf}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[#94A3B8]">Surf: Sem ranking</Badge>
                  )}
                  {profile.ranking_bodyboard ? (
                    <Badge className={`border ${RANKING_COLORS[profile.ranking_bodyboard] || ""}`} data-testid="profile-ranking-bb"><Sparkles className="mr-1 h-3 w-3" /> BB: {profile.ranking_bodyboard}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[#94A3B8]">BB: Sem ranking</Badge>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setIsRankingDialogOpen(true)} className="bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="edit-ranking-btn">
                    <Award className="mr-1 h-3.5 w-3.5" /> Rankings
                  </Button>
                  {isStaff && (
                    <Button size="sm" variant="outline" className="bg-[#6697a4]/5 border-[#6697a4]/30 text-[#6697a4] hover:bg-[#6697a4]/10" onClick={() => { setFeedbackLessonId(null); setIsFeedbackDialogOpen(true); }} data-testid="give-feedback-btn">
                      <MessageCircle className="mr-1 h-3.5 w-3.5" /> Dar Feedback
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => { setCreditAction("add"); setCreditAmount(5); setCreditType("credits"); setCreditReason("Ajuste manual"); setIsCreditsDialogOpen(true); }} data-testid="edit-credits-btn">
                      <CreditCard className="mr-1 h-3.5 w-3.5" /> Créditos
                    </Button>
                  )}
                  {isAdmin && mensalidades.length > 0 && (
                    <Button size="sm" variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50" onClick={() => setIsSubscriptionDialogOpen(true)} data-testid="assign-subscription-btn">
                      <RefreshCw className="mr-1 h-3.5 w-3.5" /> Mensalidade
                    </Button>
                  )}
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="admin-actions-btn"><UserCog className="h-3.5 w-3.5" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => handleChangeRole("professor")} data-testid="action-make-professor"><GraduationCap className="mr-2 h-4 w-4" /> Tornar Professor</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleChangeRole("admin")} data-testid="action-make-admin"><User className="mr-2 h-4 w-4" /> Tornar Admin</DropdownMenuItem>
                        <DropdownMenuItem onClick={openMergeDialog} data-testid="action-merge"><Users className="mr-2 h-4 w-4" /> Fundir com outro aluno</DropdownMenuItem>
                        <DropdownMenuItem onClick={handleBlockToggle} data-testid="action-block">
                          <Ban className="mr-2 h-4 w-4" /> {profile.blocked ? "Desbloquear" : "Bloquear"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleRemoveUser} className="text-red-600" data-testid="action-remove"><Trash2 className="mr-2 h-4 w-4" /> Remover</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 sm:grid-cols-2 gap-2 sm:gap-3 w-full sm:w-auto">
                <div className="text-center p-3 bg-slate-50 rounded-xl"><p className="text-xl sm:text-2xl font-bold text-[#6697a4]">{profile.credits}</p><p className="text-[10px] text-[#94A3B8]">Grupo</p></div>
                <div className="text-center p-3 bg-slate-50 rounded-xl"><p className="text-xl sm:text-2xl font-bold text-[#e8927c]">{profile.private_credits}</p><p className="text-[10px] text-[#94A3B8]">Privados</p></div>
                <div className="text-center p-3 bg-slate-50 rounded-xl"><p className="text-xl sm:text-2xl font-bold text-[#3d5a63]">{profile.total_lessons}</p><p className="text-[10px] text-[#94A3B8]">Aulas</p></div>
                <div className="text-center p-3 bg-slate-50 rounded-xl"><p className="text-xl sm:text-2xl font-bold text-[#F59E0B]">{activeEnrollments.length}</p><p className="text-[10px] text-[#94A3B8]">Inscritas</p></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next / Last Lesson Highlights */}
        {(nextLesson || lastLesson) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <LessonHighlight enrollment={nextLesson} label="Próxima Aula" color="bg-[#6697a4]/10" icon={Calendar} />
            <LessonHighlight enrollment={lastLesson} label="Última Aula" color="bg-slate-100" icon={Clock} />
          </div>
        )}

        {/* Tabs: Caderneta first, then Histórico */}
        <Tabs defaultValue="logbook" className="space-y-4">
          <TabsList className="bg-white border border-slate-100 p-1 rounded-xl w-full sm:w-auto">
            <TabsTrigger value="logbook" className="rounded-lg data-[state=active]:bg-[#6697a4] data-[state=active]:text-white flex-1 sm:flex-initial" data-testid="tab-logbook">
              <Award className="mr-1.5 h-4 w-4" /> Caderneta
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-[#6697a4] data-[state=active]:text-white flex-1 sm:flex-initial" data-testid="tab-history">
              <BookOpen className="mr-1.5 h-4 w-4" /> Histórico ({profile.enrollments?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="logbook">
            <Card className="bg-white border-0 shadow-sm rounded-xl">
              <CardContent className="p-4 sm:p-6">
                <Logbook userId={userId} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <div className="space-y-3">
              {activeEnrollments.length > 0 && (
                <h3 className="text-sm font-semibold text-[#6697a4] uppercase tracking-wider">Inscrições Ativas</h3>
              )}
              {activeEnrollments.map((e) => (
                <Card key={e.enrollment_id} className="bg-white border-0 shadow-sm rounded-xl" data-testid={`history-${e.enrollment_id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#3d5a63] truncate">{e.lesson?.title || "Aula"}</p>
                      <p className="text-sm text-[#94A3B8]">{e.lesson?.date && format(parseISO(e.lesson.date), "d MMM yyyy", { locale: pt })} — {e.lesson?.start_time}</p>
                    </div>
                    <Badge className={
                      e.status === "reserved" ? "bg-[#F59E0B] text-white border-0 shrink-0" :
                      e.status === "enrolled" ? "bg-[#6697a4] text-white border-0 shrink-0" :
                      "bg-[#3B82F6] text-white border-0 shrink-0"
                    }>
                      {e.status === "reserved" ? "Reservado" : e.status === "enrolled" ? "Inscrito" : "Confirmado"}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
              {completedEnrollments.length > 0 && (
                <h3 className="text-sm font-semibold text-[#10B981] uppercase tracking-wider mt-4">Aulas Completadas</h3>
              )}
              {completedEnrollments.map((e) => {
                const l = e.lesson;
                const hasTeachers = l?.teachers_data && l.teachers_data.length > 0;
                const categoryLabel = l?.lesson_category === "bodyboard" ? "Bodyboard" : l?.lesson_category === "both" ? "Surf & BB" : "Surf";
                const typeLabel = l?.lesson_type === "private" ? "Privada" : "Grupo";
                
                return (
                  <Card key={e.enrollment_id} className="bg-white border-0 shadow-sm rounded-xl hover:shadow-md transition-shadow" data-testid={`completed-lesson-${e.enrollment_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          {/* Lesson title and badges */}
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-semibold text-[#3d5a63]">{l?.title || "Aula"}</p>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">{categoryLabel}</Badge>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">{typeLabel}</Badge>
                          </div>
                          
                          {/* Date and time */}
                          <div className="flex items-center gap-4 text-sm text-[#475569] mb-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5 text-[#94A3B8]" />
                              {l?.date && format(parseISO(l.date), "d MMM yyyy", { locale: pt })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5 text-[#94A3B8]" />
                              {l?.start_time} - {l?.end_time || "?"}
                            </span>
                          </div>
                          
                          {/* Teachers */}
                          {hasTeachers && (
                            <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                              <GraduationCap className="h-3.5 w-3.5" />
                              {l.teachers_data.map(t => t.name).join(", ")}
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {isStaff && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-xs bg-[#6697a4]/5 border-[#6697a4]/30 text-[#6697a4] hover:bg-[#6697a4]/10"
                              onClick={() => { setFeedbackLessonId(l?.lesson_id); setIsFeedbackDialogOpen(true); }}
                              data-testid={`feedback-btn-${e.enrollment_id}`}
                            >
                              <MessageCircle className="mr-1 h-3 w-3" /> Feedback
                            </Button>
                          )}
                          <Badge className="bg-[#10B981] text-white border-0"><Check className="mr-1 h-3 w-3" /> Completada</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {profile.enrollments?.length === 0 && (
                <p className="text-[#94A3B8] text-center py-8">Sem histórico de aulas</p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Ranking Dialog */}
        <Dialog open={isRankingDialogOpen} onOpenChange={setIsRankingDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Rankings — {profile.name}</DialogTitle></DialogHeader>
            <div className="space-y-6">
              {["surf", "bodyboard"].map(mod => {
                const current = mod === "surf" ? rankingSurf : rankingBodyboard;
                return (
                  <div key={mod}>
                    <Label className="text-base font-semibold text-[#3d5a63] mb-3 block capitalize">{mod === "surf" ? "Surf" : "Bodyboard"}</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant={!current ? "default" : "outline"} className={!current ? "bg-[#94A3B8]" : ""} onClick={() => handleUpdateRanking(mod, "")} data-testid={`sp-ranking-${mod}-none`}>Sem ranking</Button>
                      {RANKING_LEVELS.map(level => (
                        <Button key={level} size="sm" variant={current === level ? "default" : "outline"} onClick={() => handleUpdateRanking(mod, level)} data-testid={`sp-ranking-${mod}-${level.toLowerCase()}`}>
                          <span className={`w-3 h-3 rounded-full mr-1.5 border ${RANKING_COLORS[level]}`}></span>{level}
                        </Button>
                      ))}
                    </div>
                    {current && <p className="text-sm text-[#6697a4] mt-2">Atual: <strong>{current}</strong></p>}
                  </div>
                );
              })}
            </div>
            <DialogFooter><DialogClose asChild><Button variant="outline">Fechar</Button></DialogClose></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credits Dialog */}
        {isAdmin && (
          <Dialog open={isCreditsDialogOpen} onOpenChange={setIsCreditsDialogOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>Gerir Créditos — {profile.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-xl"><p className="text-sm text-[#94A3B8]">Grupo</p><p className="text-3xl font-bold text-[#6697a4]">{profile.credits}</p></div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl"><p className="text-sm text-[#94A3B8]">Privados</p><p className="text-3xl font-bold text-[#e8927c]">{profile.private_credits}</p></div>
                </div>
                <div><Label>Tipo</Label><Select value={creditType} onValueChange={setCreditType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="credits">Grupo</SelectItem><SelectItem value="private_credits">Privado</SelectItem></SelectContent></Select></div>
                <div><Label>Ação</Label><Select value={creditAction} onValueChange={setCreditAction}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="add">Adicionar</SelectItem><SelectItem value="subtract">Remover</SelectItem><SelectItem value="set">Definir</SelectItem></SelectContent></Select></div>
                <div><Label>Quantidade</Label><Input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value === '' ? '' : parseInt(e.target.value))} onBlur={(e) => setCreditAmount(parseInt(e.target.value) || 0)} min={0} /></div>
                <div><Label>Motivo</Label><Input value={creditReason} onChange={(e) => setCreditReason(e.target.value)} /></div>
              </div>
              <DialogFooter>
                <DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose>
                <Button onClick={handleUpdateCredits} className="bg-[#6697a4] hover:bg-[#3d5a63]">Confirmar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Feedback Dialog */}
        <ProfessorFeedback
          open={isFeedbackDialogOpen}
          onOpenChange={setIsFeedbackDialogOpen}
          student={profile}
          onSuccess={fetchProfile}
          lang="pt"
          initialLessonId={feedbackLessonId}
        />

        {/* Subscription Dialog */}
        {isAdmin && (
          <Dialog open={isSubscriptionDialogOpen} onOpenChange={setIsSubscriptionDialogOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>Atribuir Mensalidade — {profile.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Plano</Label>
                  <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                    <SelectTrigger data-testid="sp-plan-select"><SelectValue placeholder="Selecionar plano..." /></SelectTrigger>
                    <SelectContent>
                      {mensalidades.map(m => (
                        <SelectItem key={m.service_id} value={m.service_id}>
                          {m.name} — {m.price}€ / {m.weekly_credits || 0} cred/semana
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAssignSubscription} className="w-full bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="confirm-assign-subscription">
                  Atribuir Mensalidade
                </Button>
              </div>
              <DialogFooter>
                <DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Merge Students Dialog */}
        {isAdmin && (
          <Dialog open={isMergeDialogOpen} onOpenChange={setIsMergeDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" /> Fundir Utilizadores
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Step 1: Search for target user */}
                {!mergeTarget && (
                  <div className="space-y-3">
                    <Label>Pesquisar aluno para fundir com {profile.name}</Label>
                    <p className="text-sm text-[#94A3B8]">
                      O perfil atual ({profile.name}) será <strong>eliminado</strong> e os dados serão transferidos para o aluno selecionado.
                    </p>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        value={mergeSearch}
                        onChange={(e) => searchMergeTargets(e.target.value)}
                        placeholder="Pesquisar por nome ou email..."
                        className="pl-10"
                        data-testid="merge-search-input"
                      />
                    </div>
                    {mergeSearchResults.length > 0 && (
                      <div className="border rounded-lg max-h-48 overflow-y-auto">
                        {mergeSearchResults.map(s => (
                          <div
                            key={s.user_id}
                            className="p-3 hover:bg-slate-50 cursor-pointer flex items-center justify-between border-b last:border-b-0"
                            onClick={() => selectMergeTarget(s)}
                            data-testid={`merge-option-${s.user_id}`}
                          >
                            <div>
                              <p className="font-medium text-[#3d5a63]">{s.name}</p>
                              <p className="text-sm text-[#94A3B8]">{s.email}</p>
                            </div>
                            <Badge variant="outline">{s.credits || 0} créditos</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Compare and configure merge */}
                {mergeTarget && sourcePreview && mergeTargetPreview && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Button variant="ghost" size="sm" onClick={() => { setMergeTarget(null); setMergeTargetPreview(null); }}>
                        <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
                      </Button>
                    </div>

                    {/* Side by side comparison */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Source (will be deleted) */}
                      <Card className="border-red-200 bg-red-50/30">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-red-500 text-white">Será eliminado</Badge>
                          </div>
                          <h4 className="font-bold text-[#3d5a63]">{sourcePreview.name}</h4>
                          <p className="text-sm text-[#94A3B8] mb-3">{sourcePreview.email}</p>
                          <div className="space-y-1 text-xs text-[#475569]">
                            <p>📱 {sourcePreview.phone || "Sem telefone"}</p>
                            <p>🎓 Créditos grupo: {sourcePreview.credits}</p>
                            <p>🎓 Créditos privados: {sourcePreview.private_credits}</p>
                            <p>📚 Inscrições: {sourcePreview.enrollments_count}</p>
                            <p>💬 Feedback: {sourcePreview.feedback_count}</p>
                            <p>🛒 Compras: {sourcePreview.purchases_count}</p>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Target (will remain) */}
                      <Card className="border-green-200 bg-green-50/30">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-green-500 text-white">Será mantido</Badge>
                          </div>
                          <h4 className="font-bold text-[#3d5a63]">{mergeTargetPreview.name}</h4>
                          <p className="text-sm text-[#94A3B8] mb-3">{mergeTargetPreview.email}</p>
                          <div className="space-y-1 text-xs text-[#475569]">
                            <p>📱 {mergeTargetPreview.phone || "Sem telefone"}</p>
                            <p>🎓 Créditos grupo: {mergeTargetPreview.credits}</p>
                            <p>🎓 Créditos privados: {mergeTargetPreview.private_credits}</p>
                            <p>📚 Inscrições: {mergeTargetPreview.enrollments_count}</p>
                            <p>💬 Feedback: {mergeTargetPreview.feedback_count}</p>
                            <p>🛒 Compras: {mergeTargetPreview.purchases_count}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Merge options */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <h4 className="font-semibold text-[#3d5a63]">Opções de Fusão</h4>
                      
                      {/* Profile data choices */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs">Email a manter</Label>
                          <Select value={mergeOptions.keep_email} onValueChange={(v) => setMergeOptions({...mergeOptions, keep_email: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="target">{mergeTargetPreview.email}</SelectItem>
                              <SelectItem value="source">{sourcePreview.email}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Nome a manter</Label>
                          <Select value={mergeOptions.keep_name} onValueChange={(v) => setMergeOptions({...mergeOptions, keep_name: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="target">{mergeTargetPreview.name || "(vazio)"}</SelectItem>
                              <SelectItem value="source">{sourcePreview.name || "(vazio)"}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Telefone a manter</Label>
                          <Select value={mergeOptions.keep_phone} onValueChange={(v) => setMergeOptions({...mergeOptions, keep_phone: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="target">{mergeTargetPreview.phone || "(vazio)"}</SelectItem>
                              <SelectItem value="source">{sourcePreview.phone || "(vazio)"}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Transfer options */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="transfer_credits" 
                            checked={mergeOptions.transfer_credits}
                            onCheckedChange={(v) => setMergeOptions({...mergeOptions, transfer_credits: v})}
                          />
                          <Label htmlFor="transfer_credits" className="text-sm cursor-pointer">
                            Transferir créditos grupo (+{sourcePreview.credits})
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="transfer_private_credits" 
                            checked={mergeOptions.transfer_private_credits}
                            onCheckedChange={(v) => setMergeOptions({...mergeOptions, transfer_private_credits: v})}
                          />
                          <Label htmlFor="transfer_private_credits" className="text-sm cursor-pointer">
                            Transferir créditos privados (+{sourcePreview.private_credits})
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="transfer_enrollments" 
                            checked={mergeOptions.transfer_enrollments}
                            onCheckedChange={(v) => setMergeOptions({...mergeOptions, transfer_enrollments: v})}
                          />
                          <Label htmlFor="transfer_enrollments" className="text-sm cursor-pointer">
                            Transferir inscrições ({sourcePreview.enrollments_count})
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="transfer_feedback" 
                            checked={mergeOptions.transfer_feedback}
                            onCheckedChange={(v) => setMergeOptions({...mergeOptions, transfer_feedback: v})}
                          />
                          <Label htmlFor="transfer_feedback" className="text-sm cursor-pointer">
                            Transferir feedback ({sourcePreview.feedback_count})
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="transfer_purchases" 
                            checked={mergeOptions.transfer_purchases}
                            onCheckedChange={(v) => setMergeOptions({...mergeOptions, transfer_purchases: v})}
                          />
                          <Label htmlFor="transfer_purchases" className="text-sm cursor-pointer">
                            Transferir compras ({sourcePreview.purchases_count})
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="transfer_subscriptions" 
                            checked={mergeOptions.transfer_subscriptions}
                            onCheckedChange={(v) => setMergeOptions({...mergeOptions, transfer_subscriptions: v})}
                          />
                          <Label htmlFor="transfer_subscriptions" className="text-sm cursor-pointer">
                            Transferir subscrições ({sourcePreview.subscriptions_count})
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="transfer_rankings" 
                            checked={mergeOptions.transfer_rankings}
                            onCheckedChange={(v) => setMergeOptions({...mergeOptions, transfer_rankings: v})}
                          />
                          <Label htmlFor="transfer_rankings" className="text-sm cursor-pointer">
                            Transferir rankings (se destino não tiver)
                          </Label>
                        </div>
                      </div>
                    </div>

                    {/* Warning */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        ⚠️ <strong>Atenção:</strong> Esta ação é irreversível. O perfil de "{sourcePreview.name}" será permanentemente eliminado.
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => setIsMergeDialogOpen(false)}>Cancelar</Button>
                      <Button 
                        onClick={handleMerge} 
                        disabled={merging}
                        className="bg-[#6697a4] hover:bg-[#3d5a63]"
                        data-testid="confirm-merge-btn"
                      >
                        {merging ? "A fundir..." : "Confirmar Fusão"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
    </div>
  );
};

export default StudentProfile;
